import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { homeHeroImage, logoImage, revImage, splitPanelImage, faqImage } from '../assets/images';
import api from '../utils/api';
import './Home.css';

const faqs = [
  {
    q: 'How many driving lessons will I need?',
    a: ' The number of lessons depends on your experience and learning pace. However, many learners in the UK need around 30 hours of professional driving lessons on average, and your instructor will guide you based on your progress.'
  },
  {
    q: 'Do you offer automatic driving lessons?',
    a: 'Yes. We offer both manual and automatic driving lessons with DVSA-approved instructors.'
  },
  {
    q: 'What are discounted lesson packages?',
    a: 'Discounted lesson packages allow learners to book lesson hours at a reduced price compared to individual lessons. The more hours you book, the more you save.'
  },
  {
    q: 'How do I pay for lessons?',
    a: 'Payments are processed securely online via Stripe. You pay an upfront booking fee to confirm your lesson; remaining amounts are agreed directly with your instructor.'
  },
  {
    q: 'Are your instructors DVSA approved?',
    a: 'Yes. All instructors on Fast Track Driving School are DVSA-approved ADI instructors who hold a current Green Badge.'
  }
];

const reviews = [
  {
    name: 'Sarah M.',
    area: 'East London',
    time: '2 days ago',
    title: 'Passed first time — couldn\'t be happier!',
    text: 'Brilliant service. I was matched with a patient instructor within 2 days and passed my test first time after 6 weeks. Fast Track made the whole process so easy.',
    stars: 5
  },
  {
    name: 'James K.',
    area: 'North London',
    time: '5 days ago',
    title: 'Easy booking, great instructor, great value.',
    text: 'Loved how easy the booking process was. My instructor was professional and kept lessons fun. The discounted intro lesson was brilliant value too.',
    stars: 5
  },
  {
    name: 'Priya R.',
    area: 'Barking',
    time: '1 week ago',
    title: 'A very supportive and patient instructor.',
    text: 'As a nervous driver I needed extra patience. My Fast Track instructor was fantastic — calm, encouraging and professional. Passed within 10 weeks!',
    stars: 5
  },
  {
    name: 'Daniel O.',
    area: 'Ilford',
    time: '2 weeks ago',
    title: 'Highly recommend for anyone nervous about driving.',
    text: 'I\'d failed twice with another school. Fast Track matched me with a brilliant instructor who completely rebuilt my confidence. Passed third attempt!',
    stars: 5
  },
  {
    name: 'Aisha T.',
    area: 'Romford',
    time: '3 weeks ago',
    title: 'Seamless from start to finish.',
    text: 'The website was easy to use, the instructor arrived on time, and lessons were always well-structured. I knew exactly what to expect each week.',
    stars: 5
  },
];

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="18" fill="#00b67a"/>
    <path d="M9 3.5l1.545 3.13L14 7.23l-2.5 2.435.59 3.44L9 11.385l-3.09 1.72.59-3.44L4 7.23l3.455-.6L9 3.5z" fill="white"/>
  </svg>
);

const HalfStarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="18" fill="#dde"/>
    <rect width="9" height="18" fill="#00b67a"/>
    <path d="M9 3.5l1.545 3.13L14 7.23l-2.5 2.435.59 3.44L9 11.385l-3.09 1.72.59-3.44L4 7.23l3.455-.6L9 3.5z" fill="white"/>
  </svg>
);




function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const [siteStats, setSiteStats] = useState({ instructors: null, students: null });
  const [testCentres, setTestCentres] = useState([]);
  const [liveReviews, setLiveReviews] = useState([]);

  useEffect(() => {
    api.get('/stats')
      .then(data => setSiteStats(data))
      .catch(() => {});

    api.get('/test-centres?withInstructors=true')
      .then(data => setTestCentres((data.testCentres || []).map(tc => tc.name)))
      .catch(() => {});

    api.get('/reviews?limit=8')
      .then(data => {
        if (data?.reviews?.length > 0) setLiveReviews(data.reviews);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="home">

      {/* ===================== HERO ===================== */}
      <section className="hero">

        {/* Left — text panel */}
        <div className="hero-left">
          <div className="hero-left-inner">

            {/* Eyebrow tag */}
            <div className="hero-tag">
              <span className="hero-tag-dot"></span>
              DVSA-Approved Instructors&nbsp;&middot;&nbsp;UK-Wide
            </div>

            {/* Headline */}
            <h1 className="hero-title">
              Learn to Drive<br />
              <span className="hero-title--stroke" style={{ paddingTop: '0.25em' }}>
                With Confidence.
              </span>
            </h1>

            {/* Divider */}
            <div className="hero-divider">
              <span className="hero-divider-bar"></span>
              <span className="hero-divider-dot"></span>
            </div>

            <p className="hero-subtitle">
              Book discounted <strong>driving lesson packages</strong> with qualified local instructors. Manual &amp; automatic lessons available with flexible scheduling and secure online booking.
            </p>

            {/* CTAs */}
            <div className="hero-ctas">
              <Link to="/book-lesson" className="hero-btn hero-btn--primary">
                Book Your Lesson
              </Link>
              <Link to="/become-instructor" className="hero-btn hero-btn--ghost">
                Join as an Instructor
              </Link>
            </div>

            {/* Trust strip */}
            <div className="hero-trust">
              <div className="trust-item"><span className="trust-check">✓</span> DVSA-Approved Instructors</div>
              <div className="trust-item"><span className="trust-check">✓</span> Manual &amp; Automatic</div>
              <div className="trust-item"><span className="trust-check">✓</span> Nationwide Coverage</div>
              <div className="trust-item"><span className="trust-check">✓</span> Secure Stripe Payments</div>
            </div>

          </div>
        </div>

        {/* Right — visual panel */}
        <div className="hero-right">
          {/* Background photo */}
          <img
            src={homeHeroImage}
            alt="Driving lesson in progress"
            className="hero-photo"
          />
          <div className="hero-photo-overlay"></div>

          {/* Floating stat cards */}
          <div className="hero-stat hero-stat--1">
            <span className="hero-stat-num">
              {(() => {
                const n = (siteStats.instructors != null ? siteStats.instructors : 970) + 30;
                return n >= 1000 ? `${Math.floor(n / 1000)}k` : n;
              })()}
              <span>+</span>
            </span>
            <span className="hero-stat-label">ADI Instructors</span>
          </div>
          <div className="hero-stat hero-stat--2">
            <span className="hero-stat-num">
              {(() => {
                const n = (siteStats.students != null ? siteStats.students : 4970) + 30;
                return n >= 1000 ? `${Math.floor(n / 1000)}k` : n;
              })()}
              <span>+</span>
            </span>
            <span className="hero-stat-label">Learners Matched</span>
          </div>

          {/* Red corner accent */}
          <div className="hero-corner-accent"></div>
        </div>

      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="section section--white how-section">
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">Simple Process</span>
            <h2 className="section-title">How It Works</h2>
            <p className="section-desc">Get behind the wheel in three easy steps</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number-wrap">
                <span className="step-num">01</span>
              </div>
              <h3 className="step-title">Request Your Lesson</h3>
              <p className="step-desc">Fill out our quick booking form with your name, email, contact number, preferred transmission, test centre location, and availability.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number-wrap">
                <span className="step-num">02</span>
              </div>
              <h3 className="step-title">Get Matched with a Local Instructor</h3>
              <p className="step-desc">We review your request and match you with a DVSA-approved instructor who covers your area and matches your availability and transmission preference.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number-wrap">
                <span className="step-num">03</span>
              </div>
              <h3 className="step-title">Pay &amp; Start Driving</h3>
              <p className="step-desc">Pay the upfront booking fee securely via Stripe. Once confirmed, we share contact details between learner and instructor — lessons begin!</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOR STUDENTS / INSTRUCTORS ===================== */}
      <section className="section section--grey two-path-section">
        <div className="container">
          <div className="two-path-grid">

            {/* Learners */}
            <div className="path-card path-card--learner">
              <div className="path-icon">
                <svg viewBox="0 0 60 60" fill="none"><circle cx="30" cy="30" r="30" fill="#1043A0" fillOpacity="0.12"/><path d="M30 14a8 8 0 100 16 8 8 0 000-16zm0 18c-7.18 0-13 3.8-13 8.5V44h26v-3.5c0-4.7-5.82-8.5-13-8.5z" fill="#1043A0"/></svg>
              </div>
              <h2 className="path-title">For Learners</h2>
              <p className="path-desc">Start Learning with a DVSA-Approved Instructor. Fast Track Driving School connects learners with professional driving instructors across the UK, helping you start learning quickly and confidently.</p>
              <ul className="path-checklist">
                <li><span className="chk">✓</span> Discounted driving lesson packages</li>
                <li><span className="chk">✓</span> DVSA-approved ADI instructors</li>
                <li><span className="chk">✓</span> Manual and automatic driving lessons</li>
                <li><span className="chk">✓</span> Instructors covering major UK test centres</li>
                <li><span className="chk">✓</span> Secure Stripe payments</li>
                <li><span className="chk">✓</span> Flexible lesson scheduling to fit your availability</li>
              </ul>
              <Link to="/book-lesson" className="path-btn path-btn--blue">Book a Lesson</Link>
            </div>

            {/* Instructors */}
            <div className="path-card path-card--instructor">
              <div className="path-icon">
                <svg viewBox="0 0 60 60" fill="none"><circle cx="30" cy="30" r="30" fill="#CF142B" fillOpacity="0.12"/><path d="M46 42l-8-4v-3a10 10 0 10-16 0v3l-8 4v4h32v-4z" fill="#CF142B"/></svg>
              </div>
              <h2 className="path-title">For Instructors</h2>
              <p className="path-desc">Get More Students Without Franchise Fees. Join Fast Track and receive lesson requests from learners looking for instructors in your area. Stay independent while we handle the connections.</p>
              <ul className="path-checklist">
                <li><span className="chk">✓</span> No franchise fees</li>
                <li><span className="chk">✓</span> No monthly or weekly charges</li>
                <li><span className="chk">✓</span> Full control of your schedule</li>
                <li><span className="chk">✓</span> Students matched in your area</li>
                <li><span className="chk">✓</span> Manual &amp; automatic instructors welcome</li>
                <li><span className="chk">✓</span> Up to £50 referral bonus</li>
              </ul>
              <Link to="/become-instructor" className="path-btn path-btn--red">Become an Instructor</Link>
            </div>

          </div>
        </div>
      </section>

      {/* ===================== SERVICES GRID ===================== */}
      <section className="section section--white services-section">
        <div className="container">
          <div className="svc-header">
            <span className="svc-eyebrow">What We Offer</span>
            <h2 className="svc-heading">A Smarter Way to Connect Learners and Driving Instructors</h2>
            <p className="svc-subheading">Fast Track Driving School is designed to make learning to drive simple, affordable, and transparent for both learners and instructors.</p>
          </div>
          <div className="svc-grid">

            {/* Discounted Packages */}
            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="28" cy="28" r="20"/>
                  <path d="M20 36l16-16"/>
                  <circle cx="21" cy="21" r="3"/>
                  <circle cx="35" cy="35" r="3"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Discounted Driving Lesson Packages</h3>
              <p className="svc-desc">Learners can book discounted lesson packages online and get matched with a local DVSA-approved instructor quickly. The more hours you book, the more you save.</p>
            </div>

            {/* Fast Matching */}
            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="28" cy="18" r="9"/>
                  <path d="M13 44c0-8.28 6.72-15 15-15s15 6.72 15 15"/>
                  <path d="M38 8l5-4M44 14l4-2"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Fast Instructor Matching</h3>
              <p className="svc-desc">Once a booking is confirmed, we connect learners with a qualified instructor in their area based on location, transmission type, and availability.</p>
            </div>

            {/* DVSA-Approved */}
            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M28 6l4.5 9 10 1.5-7.25 7 1.75 10L28 29l-9 4.5 1.75-10L13.5 16.5l10-1.5z"/>
                  <path d="M20 38l-4 8M36 38l4 8M20 46h16"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Learn With DVSA-Approved Instructors</h3>
              <p className="svc-desc">All instructors in our network are DVSA-approved ADI professionals, ensuring safe and high-quality driving lessons.</p>
            </div>

            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M28 6l4.5 9 10 1.5-7.25 7 1.75 10L28 29l-9 4.5 1.75-10L13.5 16.5l10-1.5z"/>
                  <path d="M20 38l-4 8M36 38l4 8M20 46h16"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Nationwide Coverage Across the UK</h3>
              <p className="svc-desc"> Learners can book driving lessons anywhere in the UK through our platform. We connect students with trusted local instructors, making it easy to start learning no matter where you are located.</p>
            </div>

            {/* Flexible Options */}
            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="8" y="14" width="40" height="30" rx="4"/>
                  <path d="M8 22h40"/>
                  <path d="M18 8v6M38 8v6"/>
                  <path d="M16 30h8M16 36h14"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Flexible Learning Options</h3>
              <p className="svc-desc">Choose between manual or automatic driving lessons and schedule lessons based on your availability.</p>
            </div>

            {/* Secure Booking */}
            <div className="svc-card">
              <div className="svc-icon-wrap">
                <svg viewBox="0 0 56 52" fill="none" stroke="var(--gb-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="10" y="22" width="36" height="24" rx="3"/>
                  <path d="M18 22v-6a10 10 0 0120 0v6"/>
                  <circle cx="28" cy="34" r="3"/>
                  <line x1="28" y1="37" x2="28" y2="41"/>
                </svg>
              </div>
              <div className="svc-rule"></div>
              <h3 className="svc-title">Simple &amp; Secure Booking</h3>
              <p className="svc-desc">Students can book lessons online in minutes with secure Stripe payments, making the process smooth and reliable.</p>
            </div>

          </div>
        </div>
      </section>


      {/* ===================== REFERRAL PROGRAMME ===================== */}
      <section className="section section--grey referral-section">
        <div className="container">
          <div className="referral-grid">

            {/* Left — text */}
            <div className="referral-text">
              <span className="section-eyebrow">Referral Programme</span>
              <h2 className="section-title" style={{ marginBottom: '1rem' }}>
                Know a Driving Instructor?<br />
                <span style={{ color: 'var(--gb-red)' }}>Earn Up to £50.</span>
              </h2>
              <p style={{ color: '#475569', lineHeight: 1.8, marginBottom: '1.5rem', fontSize: '1.05rem' }}>
                Refer a DVSA-approved driving instructor to Fast Track Driving School using your personal referral code.
                When they join and complete their first driving session, you earn a <strong>Up to £50 bonus</strong> — automatically.
              </p>
              <ul className="path-checklist" style={{ marginBottom: '2rem' }}>
                <li><span className="chk">✓</span> Share your unique referral code with an instructor</li>
                <li><span className="chk">✓</span> They sign up and get approved on the platform</li>
                <li><span className="chk">✓</span> They complete their first student session</li>
                <li><span className="chk">✓</span> You receive Up to £50 bonus— no forms, no hassle</li>
              </ul>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link to="/become-instructor" className="path-btn path-btn--red">
                  Get Your Referral Code
                </Link>
                <Link to="/become-instructor" className="path-btn" style={{ background: 'transparent', border: '2px solid #0A1F4E', color: '#0A1F4E' }}>
                  Learn More
                </Link>
              </div>
            </div>

            {/* Right — visual card */}
            <div className="referral-card-wrap">
              <div className="referral-card">
                {/* Top accent bar */}
                <div className="referral-card-bar"></div>
                <div className="referral-card-inner">
                  <div className="referral-icon-wrap">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="24" fill="#CF142B" fillOpacity="0.1"/>
                      <path d="M24 12a6 6 0 100 12 6 6 0 000-12zm-10 20c0-5.52 4.48-10 10-10s10 4.48 10 10" stroke="#CF142B" strokeWidth="2.2" strokeLinecap="round"/>
                      <circle cx="36" cy="18" r="5" fill="#CF142B" fillOpacity="0.15" stroke="#CF142B" strokeWidth="2"/>
                      <path d="M36 15v3l2 1.5" stroke="#CF142B" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 className="referral-card-title">Referral Bonus</h3>
                  <div className="referral-amount">Up to £50</div>
                  <p className="referral-card-sub">per instructor you refer</p>
                  <div className="referral-steps">
                    {[
                      { step: '1', text: 'Share your referral code' },
                      { step: '2', text: 'Instructor joins Fast Track' },
                      { step: '3', text: 'They complete first session' },
                      { step: '4', text: 'You earn Up to £50 bonus' },
                    ].map(s => (
                      <div className="referral-step" key={s.step}>
                        <span className="referral-step-num">{s.step}</span>
                        <span className="referral-step-text">{s.text}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/become-instructor" className="referral-cta-btn">
                    Start Referring →
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===================== WHY CHOOSE US ===================== */}
      <section className="section section--navy why-section">
        <div className="container">
          <div className="section-header section-header--light">
            <span className="section-eyebrow section-eyebrow--light">Why Fast Track?</span>
            <h2 className="section-title section-title--light">A Driving School Built Around You</h2>
          </div>
          <div className="why-grid">
            {[
              {
                num: (() => {
                  const n = (siteStats.instructors != null ? siteStats.instructors : 970) + 30;
                  return n >= 1000 ? `${(n / 1000).toFixed(1)}k+` : `${n}+`;
                })(),
                label: 'Qualified Instructors',
                sub: 'ADI green badge holders across the UK'
              },
              {
                num: testCentres.length > 0 ? `${testCentres.length}+` : '20+',
                label: 'Test Centre Areas',
                sub: 'Areas where our instructors operate'
              },
              {
                num: (() => {
                  const n = (siteStats.students != null ? siteStats.students : 4970) + 30;
                  return n >= 1000 ? `${(n / 1000).toFixed(1)}k+` : `${n}+`;
                })(),
                label: 'Learners Matched',
                sub: 'Students paired and lessons started'
              },
              { num: '100%', label: 'DVSA Approved', sub: 'Every instructor is fully qualified' },
            ].map((w, i) => (
              <div className="why-stat" key={i}>
                <span className="why-num">{w.num}</span>
                <span className="why-label">{w.label}</span>
                <span className="why-sub">{w.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== REVIEWS ===================== */}
      <section className="rev-section">
        <div className="rev-inner">

          {/* Heading sits above the two columns */}
          <div className="rev-header">
            <span className="rev-eyebrow">Local Reviews</span>
            <h2 className="rev-heading">What Our Learners Across the UK Say</h2>
          </div>

          <div className="rev-grid">

            {/* Left — photo */}
            <div className="rev-image-col">
              <img
                src={revImage}
                alt="Learner driver at the wheel"
                className="rev-img"
              />
            </div>

            {/* Right — trust score + scrollable feed */}
            <div className="rev-content">

              {/* Trustpilot-style score */}
              <div className="rev-trust">
                <div className="rev-trust-score">
                  <span className="rev-trust-word">Excellent</span>
                  <div className="rev-trust-stars">
                    {[1,2,3,4].map(n => (
                      <span className="rev-tp-star" key={n}>
                        <svg width="24" height="24" viewBox="0 0 24 24"><rect width="24" height="24" fill="#00b67a"/><path d="M12 4.5l2 4.2 4.5.65-3.25 3.17.77 4.48L12 14.77l-4.02 2.23.77-4.48L5.5 9.35l4.5-.65z" fill="#fff"/></svg>
                      </span>
                    ))}
                    <span className="rev-tp-star rev-tp-half">
                      <svg width="24" height="24" viewBox="0 0 24 24"><rect width="24" height="24" fill="#dde1e9"/><rect width="12" height="24" fill="#00b67a"/><path d="M12 4.5l2 4.2 4.5.65-3.25 3.17.77 4.48L12 14.77l-4.02 2.23.77-4.48L5.5 9.35l4.5-.65z" fill="#fff"/></svg>
                    </span>
                  </div>
                </div>
                <p className="rev-trust-based">Based on <strong>500+</strong> reviews from<br />verified UK learners</p>
              </div>

              <div className="rev-feed-header">
                <p className="rev-feed-label">
                  {liveReviews.length > 0
                    ? `Showing ${liveReviews.length} verified reviews`
                    : 'Showing our favourite reviews'}
                </p>
                <a
                  href="https://g.page/r/CRuj40rUiPSqEAE/review"
                  target="_blank" rel="noopener noreferrer"
                  className="rev-google-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Review on Google
                </a>
              </div>

              {/* Scrollable feed */}
              <div className="rev-feed">
                {(liveReviews.length > 0 ? liveReviews : reviews).map((r, i) => (
                  <div className={`rev-item${r.is_google ? ' rev-item--google' : ''}`} key={r.id || i}>
                    <div className="rev-item-top">
                      <div className="rev-item-stars">
                        {Array.from({ length: r.rating || r.stars || 5 }).map((_, j) => (
                          <svg key={j} width="18" height="18" viewBox="0 0 24 24">
                            <rect width="24" height="24" fill="#00b67a"/>
                            <path d="M12 4.5l2 4.2 4.5.65-3.25 3.17.77 4.48L12 14.77l-4.02 2.23.77-4.48L5.5 9.35l4.5-.65z" fill="#fff"/>
                          </svg>
                        ))}
                      </div>
                      {r.is_google ? (
                        <span className="rev-google-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Google
                        </span>
                      ) : (
                        <span className="rev-verified-badge">✓ Verified</span>
                      )}
                    </div>
                    <p className="rev-item-meta">
                      {r.reviewer_name || r.name}
                      {(r.reviewer_area || r.area) && <>, <span className="rev-item-area">{r.reviewer_area || r.area}</span></>}
                      {r.instructor_name && <> · <span className="rev-item-instr">with {r.instructor_name}</span></>}
                      <span className="rev-item-time">&nbsp;&middot;&nbsp;{r.time || new Date(r.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>
                    </p>
                    {(r.title) && <p className="rev-item-title">{r.title}</p>}
                    <p className="rev-item-body">{r.body || r.text}</p>
                  </div>
                ))}
              </div>

              {/* Leave a review CTA */}
              <div className="rev-leave">
                <p className="rev-leave-prompt">Had a lesson with Fast Track?</p>
                <Link to="/leave-review" className="rev-leave-btn">Leave a Review →</Link>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ===================== BOOK ONLINE SPLIT PANEL ===================== */}
      <section className="cb-section">
        <div className="cb-grid">

          {/* Left — decorative panel (replace /images/book-online-hero.jpg to activate photo) */}
          <div className="cb-image-wrap">
            {/* Concentric rings from top-left */}
            <div className="cb-deco-ring cb-deco-ring--1"></div>
            <div className="cb-deco-ring cb-deco-ring--2"></div>
            <div className="cb-deco-ring cb-deco-ring--3"></div>
            {/* Diagonal red slash */}
            <div className="cb-deco-slash"></div>
            {/* Ghost watermark */}
            <div className="cb-deco-ghost" aria-hidden="true">DRIVE</div>
            {/* Red L-frame accent */}
            <div className="cb-frame-accent"></div>
            {/* Floating stat badge */}
            <div className="cb-deco-badge">
              <span className="cb-deco-num">[{siteStats.instructors != null ? siteStats.instructors + 30 : 970}+]</span>
              <span className="cb-deco-label">DVSA Instructors</span>
            </div>
            {/* Photo */}
            <img
              src={splitPanelImage}
              alt="Student driver with instructor"
              className="cb-img"
            />
          </div>

          {/* Right — two stacked panels */}
          <div className="cb-panels">

            {/* Top panel — light */}
            <div className="cb-panel cb-panel--light">
              <span className="cb-eyebrow">Fast &amp; Simple.</span>
              <h2 className="cb-heading">Book Your Lesson<br />Online in Minutes.</h2>
              <p className="cb-body">
                Starting your driving journey is quick and simple. Complete our online booking form with your details, and we’ll match you with a qualified DVSA-approved driving instructor in your area.
              </p>
              <a href="tel:07534738009" className="cb-phone">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="currentColor"/>
                </svg>
                07534 738009
              </a>
              <p className="cb-hours"><strong>MON–SAT</strong> 9:00 AM – 6:00 PM</p>
            </div>

            {/* Bottom panel — dark */}
            <div className="cb-panel cb-panel--dark">
              <span className="cb-dark-eyebrow">Find Your Area</span>
              <h3 className="cb-dark-heading">Discover Test Centres<br />Across the UK</h3>
              <Link to="/about" className="cb-areas-btn">
                See All Areas <span className="cb-btn-arrow">&#9654;</span>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="section section--grey faq-section">
        <div className="container">
          <div className="faq-layout">

            {/* Left — accordion */}
            <div className="faq-col">
              <span className="section-eyebrow">FAQs</span>
              <h2 className="section-title faq-col-title">Got questions?<br />We've got answers.</h2>
              <div className="faq-list">
                {faqs.map((faq, i) => (
                  <div className={`faq-item${openFaq === i ? ' faq-item--open' : ''}`} key={i}>
                    <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <span>{faq.q}</span>
                      <span className="faq-icon">
                        {openFaq === i
                          ? <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="10" stroke="currentColor" strokeWidth="1.8"/><line x1="6" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                          : <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="10" stroke="currentColor" strokeWidth="1.8"/><line x1="11" y1="6" x2="11" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        }
                      </span>
                    </button>
                    <div className="faq-answer-wrap">
                      <div className="faq-answer">{faq.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — image panel */}
            <div className="faq-image-col">
              <div className="faq-image-frame">
                <div className="faq-image-placeholder">
                  {/* FAQ instructor photo */}
                  <img
                    src={faqImage}
                    alt="DVSA-approved instructor with learner"
                    className="faq-img"
                  />
                  <div className="faq-image-badge">
                    <span className="faq-badge-num">98%</span>
                    <span className="faq-badge-label">Student satisfaction</span>
                  </div>
                </div>
              </div>
              <div className="faq-cta-card">
                <p>Still have questions?</p>
                <a href="/contact" className="faq-cta-link">Contact our team →</a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="cta-banner">
        <div className="container cta-banner-inner">
          <div className="cta-banner-text">
            <h2 className="cta-banner-title">Ready to Start Your Driving Journey?</h2>
            <p className="cta-banner-desc">Book your discounted driving lesson package today — flexible options available.</p>
          </div>
          <div className="cta-banner-actions">
            <Link to="/book-lesson" className="cta-btn cta-btn--white">Book a Lesson Now</Link>
            <Link to="/become-instructor" className="cta-btn cta-btn--outline">Join as Instructor</Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;

